(function () {
  const SWIPE_THRESHOLD = 50;
  const TRANSITION_FALLBACK_MS = 700;

  class PrimiProductsCarousel {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-primi-track]');
      this.viewport = section.querySelector('[data-primi-viewport]');
      this.prevBtn = section.querySelector('[data-primi-prev]');
      this.nextBtn = section.querySelector('[data-primi-next]');
      this.titleEl = section.querySelector('[data-primi-active-title]');
      this.variantInput = section.querySelector('[data-primi-variant-id]');

      if (!this.track) return;

      this.removeClones();

      this.realSlides = [...this.track.querySelectorAll('[data-primi-slide]')];
      this.realCount = this.realSlides.length;
      if (this.realCount === 0) return;

      this.realSlides.forEach((slide, i) => {
        slide.dataset.logicalIndex = String(i);
      });

      this.cloneCount = this.realCount >= 2 ? Math.min(2, this.realCount - 1) : 0;
      this.buildClones();

      this.slides = [...this.track.querySelectorAll('[data-primi-slide]')];

      const defaultIndex = parseInt(section.dataset.defaultIndex || '1', 10) - 1;
      const clampedDefault = Math.min(Math.max(defaultIndex, 0), this.realCount - 1);
      this.trackIndex = this.cloneCount + clampedDefault;

      this.isAnimating = false;
      this.fallbackTimer = null;

      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);

      if (this.realCount < 2) {
        this.prevBtn?.setAttribute('hidden', '');
        this.nextBtn?.setAttribute('hidden', '');
      } else {
        this.prevBtn?.addEventListener('click', this.onPrev);
        this.nextBtn?.addEventListener('click', this.onNext);
      }

      this.slideClickHandlers = [];
      this.slides.forEach((slide, index) => {
        const handler = () => this.onSlideClick(index);
        slide.addEventListener('click', handler);
        this.slideClickHandlers.push({ slide, handler });
      });

      if (this.viewport && this.realCount >= 2) {
        this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.viewport.addEventListener('touchend', this.onTouchEnd, { passive: true });
      }

      this.track.addEventListener('transitionend', this.onTransitionEnd);
      window.addEventListener('resize', this.onResize);

      this.goTo(this.trackIndex, false);
    }

    buildClones() {
      if (this.cloneCount === 0) return;

      for (let i = 0; i < this.cloneCount; i++) {
        const clone = this.realSlides[i].cloneNode(true);
        clone.setAttribute('data-primi-clone', 'end');
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.logicalIndex = String(i);
        this.track.appendChild(clone);
      }

      const firstReal = this.realSlides[0];
      for (let i = 0; i < this.cloneCount; i++) {
        const realIdx = this.realCount - this.cloneCount + i;
        const clone = this.realSlides[realIdx].cloneNode(true);
        clone.setAttribute('data-primi-clone', 'start');
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.logicalIndex = String(realIdx);
        this.track.insertBefore(clone, firstReal);
      }
    }

    removeClones() {
      this.track
        .querySelectorAll('[data-primi-clone]')
        .forEach((clone) => clone.remove());
    }

    onPrev() {
      if (this.isAnimating) return;
      this.goTo(this.trackIndex - 1);
    }

    onNext() {
      if (this.isAnimating) return;
      this.goTo(this.trackIndex + 1);
    }

    onSlideClick(index) {
      if (this.isAnimating) return;
      if (index !== this.trackIndex) {
        this.goTo(index);
      }
    }

    onTouchStart(event) {
      this.touchStartX = event.changedTouches[0].screenX;
    }

    onTouchEnd(event) {
      if (this.touchStartX == null) return;
      const delta = event.changedTouches[0].screenX - this.touchStartX;
      if (Math.abs(delta) >= SWIPE_THRESHOLD) {
        if (delta > 0) this.onPrev();
        else this.onNext();
      }
      this.touchStartX = null;
    }

    onResize() {
      this.updatePosition(false);
    }

    onTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') {
        return;
      }
      this.finishTransition();
    }

    finishTransition() {
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
      this.normalizeLoop();
      this.isAnimating = false;
    }

    normalizeLoop() {
      if (this.cloneCount === 0) return;

      let newIndex = this.trackIndex;
      if (this.trackIndex < this.cloneCount) {
        newIndex = this.trackIndex + this.realCount;
      } else if (this.trackIndex >= this.cloneCount + this.realCount) {
        newIndex = this.trackIndex - this.realCount;
      }

      if (newIndex !== this.trackIndex) {
        this.track.classList.add('is-snapping');
        this.trackIndex = newIndex;
        this.applyActiveState();
        this.updatePosition(false);
        void this.track.offsetHeight;
        requestAnimationFrame(() => {
          this.track.classList.remove('is-snapping');
        });
      }
    }

    scheduleUpdatePosition(animate = true) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.updatePosition(animate);
        });
      });
    }

    goTo(index, animate = true) {
      this.trackIndex = index;
      this.applyActiveState();

      if (animate) {
        this.isAnimating = true;
        if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
        this.fallbackTimer = setTimeout(() => this.finishTransition(), TRANSITION_FALLBACK_MS);
      }

      this.scheduleUpdatePosition(animate);
      this.updateFooter();
    }

    applyActiveState() {
      this.slides.forEach((slide, i) => {
        const active = i === this.trackIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    updatePosition(animate = true) {
      const activeSlide = this.slides[this.trackIndex];
      const viewport = this.viewport || this.track.parentElement;
      if (!activeSlide || !viewport) return;

      if (!animate) {
        this.track.style.transition = 'none';
      }

      const slideCenter = activeSlide.offsetLeft + activeSlide.offsetWidth / 2;
      const offset = slideCenter - viewport.clientWidth / 2;

      this.track.style.transform = `translateX(${-offset}px)`;

      if (!animate) {
        this.track.offsetHeight;
        this.track.style.transition = '';
      }
    }

    updateFooter() {
      const activeSlide = this.slides[this.trackIndex];
      if (!activeSlide) return;

      const title = activeSlide.dataset.title || '';
      const variantId = activeSlide.dataset.variantId || '';

      if (this.titleEl) {
        this.titleEl.textContent = title;
      }

      if (this.variantInput) {
        this.variantInput.value = variantId;
      }
    }

    destroy() {
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
      this.prevBtn?.removeEventListener('click', this.onPrev);
      this.nextBtn?.removeEventListener('click', this.onNext);
      this.viewport?.removeEventListener('touchstart', this.onTouchStart);
      this.viewport?.removeEventListener('touchend', this.onTouchEnd);
      this.track?.removeEventListener('transitionend', this.onTransitionEnd);
      window.removeEventListener('resize', this.onResize);
      this.slideClickHandlers?.forEach(({ slide, handler }) => {
        slide.removeEventListener('click', handler);
      });
      this.removeClones();
    }
  }

  const instances = new WeakMap();

  function initSection(section) {
    if (instances.has(section)) {
      instances.get(section).destroy();
    }
    instances.set(section, new PrimiProductsCarousel(section));
  }

  function initAll() {
    document.querySelectorAll('[data-section-type="primi-products"]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="primi-products"]');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-section-type="primi-products"]');
    if (section && instances.has(section)) {
      instances.get(section).destroy();
      instances.delete(section);
    }
  });
})();
